import { Autocomplete, TextField } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { useFilter } from '@synergy/components';
import { useIntl } from 'react-intl';

import { PaloListFilterValue, usePaloListFilterValues } from '../palo/use-palo-list-filters';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloListKey } from '../palo/palo-list-key';

/**
 * Who handles the document, chosen by name.
 *
 * The names come from the list itself — the people it really has as agents.
 * They used to come from whoever was attached to the POOLALPIN account, which
 * on the live system offered eight names that find nothing and left out four
 * people who handle 1.209 inquiries between them, because they had left the
 * account or been locked (#2231 R-128). A list that cannot answer the
 * question falls back to the contacts in hand, which is what every list did
 * before.
 *
 * It searches as you type, as every other selection beside it does.
 */
export const ResponsibleContactFilter = () => {
    const { filter, updateFilter, registerFilterMap } = useFilter();
    const { sortedByName: contacts } = useAppSelector(state => state.contacts);
    const listKey = usePaloListKey();
    const held = usePaloListFilterValues(listKey, 'responsibleContact');
    const intl = useIntl();

    const options: PaloListFilterValue[] =
        held.length > 0 ? held : contacts.map(contact => ({ value: contact.id.toString(), label: contact.fullName }));

    const onSelected = (option: PaloListFilterValue | null) => {
        filter.delete(FilterParams.ResponsibleContactId);

        if (option) {
            filter.set(FilterParams.ResponsibleContactId, option.value);
        }

        updateFilter(filter);
    };

    registerFilterMap(FilterParams.ResponsibleContactId, (contactId: unknown) =>
        'string' === typeof contactId ? options.find(option => option.value === contactId)?.label : ''
    );

    const selected = options.find(option => option.value === filter.get(FilterParams.ResponsibleContactId)) ?? null;

    return (
        <Autocomplete<PaloListFilterValue>
            options={options}
            value={selected}
            onChange={(_event, option) => onSelected(option)}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            fullWidth={true}
            size="small"
            renderInput={params => (
                <TextField {...params} label={intl.formatMessage({ id: 'app.filter.responsibleContact' })} />
            )}
        />
    );
};
